import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  MdKeyboardArrowDown,
  MdKeyboardArrowRight,
  MdOutlineDesignServices,
  MdOutlineSettings,
  MdOutlineCategory,
  MdOutlineAccountTree,
  MdOutlinePeopleAlt,
  MdOutlineMeetingRoom,
  MdOutlineAltRoute,
  MdLockOutline,
} from "react-icons/md";
import { toast } from "react-toastify";
import * as apicall from "../../components/Apicall";
import CategoriesTab from "./tabs/CategoriesTab";
import SubcategoriesTab from "./tabs/SubcategoriesTab";
import ServiceTypesTab from "./tabs/ServiceTypesTab";
import ProvidersTab from "./tabs/ProvidersTab";
import ResourcesTab from "./tabs/ResourcesTab";
import WorkflowsTab from "./tabs/WorkflowsTab";
import useViewport from "../../hooks/useViewport";

const ACTIVE_STYLE = {
  background: "linear-gradient(135deg, #C2185B 0%, #E91E63 100%)",
  color: "#ffffff",
  boxShadow: "0 4px 12px rgba(194,24,91,0.28)",
};

const INACTIVE_STYLE = {
  background: "transparent",
  color: "#6c757d",
  boxShadow: "none",
};

const LEFT_PANEL_W = 220;

// Sections that need a provider (billing) profile first; Providers and Resources do not.
const PROVIDER_GATED = ["categories", "subcategories", "service-types", "workflows"];
const PROVIDER_FIRST_MESSAGE = "Please add your provider details first to continue setup.";

function AdminSetupRevamp() {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(
    new URLSearchParams(location.search).get("tab") || "categories"
  );
  const [setupOpen, setSetupOpen] = useState(true);

  // Providers must be set up before services/specializations can be created —
  // null while we're still checking, true/false once we know.
  const [hasProviders, setHasProviders] = useState(null);
  // Tab the user actually wanted, remembered while we force them into "providers" first.
  const [pendingTab, setPendingTab] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await apicall.get("partners");
        setHasProviders(!!(res?.success && res?.data?.length > 0));
      } catch (e) {
        setHasProviders(true); // fail open — don't block setup on an API error
      }
    })();
  }, []);

  const goToSection = (section) => {
    // Resources don't depend on a billing provider profile, so they are not
    // held back by the "add a provider first" gate.
    if (hasProviders === false && section !== "providers" && section !== "resources") {
      setPendingTab(section);
      toast.info(PROVIDER_FIRST_MESSAGE);
      setActiveSection("providers");
    } else {
      setActiveSection(section);
    }
  };

  const handleProviderAdded = () => {
    setHasProviders((prev) => {
      if (prev === false) {
        setActiveSection(pendingTab || "categories");
        setPendingTab(null);
      }
      return true;
    });
  };

  // Keep activeSection in sync with ?tab= URL param when navigating within the same
  // route, gated behind provider setup being complete (see goToSection).
  // Deliberately NOT re-run when the provider check resolves: it used to reset the
  // section to the URL's tab (default Service Category) at that moment, silently
  // undoing whatever had been tapped while the request was still loading.
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab") || "categories";
    goToSection(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Once we learn there is no provider, move off a gated section - and only then.
  useEffect(() => {
    if (hasProviders === false && PROVIDER_GATED.includes(activeSection)) {
      setPendingTab(activeSection);
      toast.info(PROVIDER_FIRST_MESSAGE);
      setActiveSection("providers");
    }
  }, [hasProviders, activeSection]);

  // Below 768px the two-pane layout is replaced by a pill strip. Reactive, from the
  // shared viewport hook (this used to keep its own resize listener).
  const { width: viewportWidth, isPhone, isCoarse } = useViewport();
  const isMobile = viewportWidth < 768;
  const providerLocked = hasProviders === false;
  const isLocked = (key) => providerLocked && PROVIDER_GATED.includes(key);
  // After a category is created, pass the new service to ServiceTypesTab so it
  // can pre-seed the Add Service Type slider and open it automatically.
  const [pendingService, setPendingService] = useState(null);

  const handleCategoryAdded = (newService) => {
    setPendingService(newService);
    setActiveSection("service-types");
  };

  const handleSubcategoryAdded = (serviceObj) => {
    // intentionally stays on sub-categories page after adding
  };

  let allServicesData = localStorage.getItem("vendorProfile");
  if (allServicesData && allServicesData !== "undefined") {
    allServicesData = JSON.parse(allServicesData);
  }
  const isClinicService = Number(allServicesData?.industry) === 1;
  const isSportsService = Number(allServicesData?.industry) === 7;
  const isCarService = Number(allServicesData?.industry) === 8;

  // Optional bookable resources: a room, court, bay, van, or a person such as a driver.
  const resourceLabels = {
    singular: isClinicService ? "Room" : isSportsService ? "Court / Field" : isCarService ? "Bay / Vehicle" : "Resource",
    plural: isClinicService ? "Rooms" : isSportsService ? "Courts & Fields" : isCarService ? "Bays & Vehicles" : "Resources",
    typeExample: isClinicService ? "Examination Room" : isSportsService ? "Court" : isCarService ? "Workbench" : "Vehicle",
    nameExample: isClinicService ? "Room 1" : isSportsService ? "Court 1" : isCarService ? "Bay 2" : "Van 1",
  };

  const categoryLabel = isClinicService
    ? "Specialization"
    : isSportsService
    ? "Sport"
    : "Service Category";

  const subcategoryLabel = "Sub Category";

  const serviceTypesLabel = isClinicService ? "Specialization Type" : isSportsService ? "Sports Type" : "Service Types";

  const serviceLabel = isClinicService
    ? "Specialization"
    : isSportsService
    ? "Sports"
    : "Service";

  const navItemBase = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "9px 12px",
    borderRadius: "8px",
    margin: "2px 8px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: 500,
    transition: "background 0.2s, color 0.2s, box-shadow 0.2s",
    textDecoration: "none",
    border: "none",
    background: "none",
    width: "calc(100% - 16px)",
    textAlign: "left",
    minHeight: isCoarse ? "44px" : "36px",
    flexShrink: 0,
  };

  const subNavItemBase = {
    ...navItemBase,
    padding: "9px 12px 9px 28px",
    fontWeight: 400,
  };

  const navLabelStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flex: 1,
  };

  // Explains the gate on the Providers tab instead of leaving locked items to
  // look broken: they used to bounce here with only a 5s toast.
  const providerBanner =
    providerLocked && activeSection === "providers" ? (
      <div
        role="status"
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          marginBottom: "16px",
          padding: "12px 14px",
          borderRadius: "10px",
          background: "#fffbeb",
          border: "1px solid #fde68a",
          color: "#92400e",
          fontSize: "13px",
          lineHeight: 1.45,
        }}
      >
        <MdLockOutline size={18} style={{ flexShrink: 0, marginTop: "1px" }} />
        <span>
          <strong>Step 1: add your provider details.</strong> {categoryLabel}, {subcategoryLabel},{" "}
          {serviceTypesLabel} and Workflows unlock as soon as a provider is saved.
        </span>
      </div>
    ) : null;

  const lockIcon = (key) =>
    isLocked(key) ? (
      <MdLockOutline size={14} style={{ flexShrink: 0, opacity: 0.7 }} aria-label="Locked until a provider is added" />
    ) : null;

  const renderContent = () => {
    switch (activeSection) {
      case "categories":
        return (
          <CategoriesTab label={categoryLabel} onCategoryAdded={handleCategoryAdded} />
        );
      case "subcategories":
        if (isClinicService) {
          return <CategoriesTab label={categoryLabel} onCategoryAdded={handleCategoryAdded} />;
        }
        return (
          <SubcategoriesTab
            label={subcategoryLabel}
            serviceLabel={serviceLabel}
            onSubcategoryAdded={handleSubcategoryAdded}
          />
        );
      case "service-types":
        return (
          <ServiceTypesTab
            pendingService={pendingService}
            onPendingServiceConsumed={() => setPendingService(null)}
          />
        );
      case "providers":
        return <ProvidersTab onComplete={handleProviderAdded} />;
      case "resources":
        return <ResourcesTab labels={resourceLabels} />;
      case "workflows":
        return <WorkflowsTab resourceLabel={resourceLabels.singular} />;
      default:
        return null;
    }
  };

  // Mobile: pill strip navigation
  if (isMobile) {
    const mobileItems = [
      { key: "categories", label: categoryLabel },
      ...(!isClinicService ? [{ key: "subcategories", label: subcategoryLabel }] : []),
      { key: "service-types", label: serviceTypesLabel },
      { key: "providers", label: "Providers" },
      { key: "resources", label: resourceLabels.plural },
      { key: "workflows", label: "Workflows" },
    ];

    return (
      <div className="container-fluid px-2">
        {/* Mobile top nav */}
        <div
          style={{
            display: "flex",
            // Phones scroll sideways; a 744-767px tablet has room to wrap, so no
            // pill (Workflows used to) hides off the right edge.
            flexWrap: isPhone ? "nowrap" : "wrap",
            overflowX: isPhone ? "auto" : "visible",
            gap: "8px",
            padding: "12px 0",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {mobileItems.map((item) => (
            <button
              key={item.key}
              onClick={() => goToSection(item.key)}
              style={{
                flexShrink: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                minHeight: isCoarse ? "44px" : "34px",
                padding: "7px 16px",
                borderRadius: "20px",
                border: `1px solid ${activeSection === item.key ? "#C2185B" : "#e3ebf8"}`,
                fontSize: "13px",
                fontWeight: activeSection === item.key ? 600 : 400,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.2s",
                ...(activeSection === item.key
                  ? ACTIVE_STYLE
                  : { background: "#fff", color: "#6c757d" }),
              }}
            >
              {item.label}
              {lockIcon(item.key)}
            </button>
          ))}
        </div>

        {/* Mobile content */}
        <div style={{ paddingTop: "8px" }}>
          {providerBanner}
          {renderContent()}
        </div>
      </div>
    );
  }

  // Desktop: two-column layout with independent scroll on each panel.
  // height = dynamic viewport minus the navbar (60-64px, plus the impersonation
  // banner when present) minus 16px×2 content-area padding. Uses the shell's own
  // tokens - it used to hard-code 96px, wrong on tablets (60px navbar, 20px padding).
  return (
    <div
      style={{
        display: "flex",
        height: "calc(100dvh - var(--navbar-h, 64px) - var(--banner-h, 0px) - 32px)",
        overflow: "hidden",
        background: "#f2f5fa",
      }}
    >
      {/* Left navigation panel — scrolls independently */}
      <div
        style={{
          width: `${LEFT_PANEL_W}px`,
          flexShrink: 0,
          background: "#ffffff",
          borderRight: "1px solid #e3ebf8",
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: "16px",
          paddingBottom: "24px",
        }}
      >
        {/* Setup accordion group */}
        <div style={{ marginBottom: "4px" }}>
          {/* Setup group header */}
          <button
            onClick={() => setSetupOpen((prev) => !prev)}
            style={{
              ...navItemBase,
              justifyContent: "space-between",
              color: setupOpen ? "#111827" : "#6c757d",
              fontWeight: 600,
              fontSize: "12px",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              padding: "8px 12px",
              margin: "0 8px",
              width: "calc(100% - 16px)",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <MdOutlineSettings size={16} />
              <span>Setup</span>
            </div>
            {setupOpen ? (
              <MdKeyboardArrowDown size={16} />
            ) : (
              <MdKeyboardArrowRight size={16} />
            )}
          </button>

          {/* Setup sub-items */}
          {setupOpen && (
            <div style={{ marginTop: "2px" }}>
              {/* Categories */}
              <button
                onClick={() => goToSection("categories")}
                style={{
                  ...subNavItemBase,
                  ...(activeSection === "categories" ? ACTIVE_STYLE : INACTIVE_STYLE),
                }}
              >
                <MdOutlineCategory size={16} style={{ flexShrink: 0 }} />
                <span style={navLabelStyle}>{categoryLabel}</span>
                {lockIcon("categories")}
              </button>

              {/* Subcategories — hidden for clinic */}
              {!isClinicService && (
                <button
                  onClick={() => goToSection("subcategories")}
                  style={{
                    ...subNavItemBase,
                    ...(activeSection === "subcategories" ? ACTIVE_STYLE : INACTIVE_STYLE),
                  }}
                >
                  <MdOutlineAccountTree size={16} style={{ flexShrink: 0 }} />
                  <span style={navLabelStyle}>{subcategoryLabel}</span>
                  {lockIcon("subcategories")}
                </button>
              )}

              {/* Service Types (labeled "Specialization Type" for clinic) */}
              <button
                onClick={() => goToSection("service-types")}
                style={{
                  ...subNavItemBase,
                  ...(activeSection === "service-types" ? ACTIVE_STYLE : INACTIVE_STYLE),
                }}
              >
                <MdOutlineDesignServices size={16} style={{ flexShrink: 0 }} />
                <span style={navLabelStyle}>{serviceTypesLabel}</span>
                {lockIcon("service-types")}
              </button>
            </div>
          )}
        </div>

        {/* Divider */}
        <div
          style={{
            height: "1px",
            background: "#f3f4f6",
            margin: "8px 16px",
          }}
        />

        {/* Providers standalone item */}
        <button
          onClick={() => goToSection("providers")}
          style={{
            ...navItemBase,
            ...(activeSection === "providers" ? ACTIVE_STYLE : INACTIVE_STYLE),
          }}
        >
          <MdOutlinePeopleAlt size={16} style={{ flexShrink: 0 }} />
          <span style={navLabelStyle}>Providers</span>
        </button>

        {/* Resources standalone item */}
        <button
          onClick={() => goToSection("resources")}
          style={{
            ...navItemBase,
            ...(activeSection === "resources" ? ACTIVE_STYLE : INACTIVE_STYLE),
          }}
        >
          <MdOutlineMeetingRoom size={16} style={{ flexShrink: 0 }} />
          <span style={navLabelStyle}>{resourceLabels.plural}</span>
        </button>

        {/* Workflows standalone item */}
        <button
          onClick={() => goToSection("workflows")}
          style={{
            ...navItemBase,
            ...(activeSection === "workflows" ? ACTIVE_STYLE : INACTIVE_STYLE),
          }}
        >
          <MdOutlineAltRoute size={16} style={{ flexShrink: 0 }} />
          <span style={navLabelStyle}>Workflows</span>
          {lockIcon("workflows")}
        </button>
      </div>

      {/* Main content area — scrolls independently */}
      {/* overflowY: "scroll" keeps scrollbar space reserved so layout width
          stays stable when switching between tabs with different content heights. */}
      <div
        style={{
          flex: 1,
          overflowY: "scroll",
          overflowX: "hidden",
          padding: "24px",
          minWidth: 0,
        }}
      >
        {providerBanner}
        {renderContent()}
      </div>
    </div>
  );
}

export default AdminSetupRevamp;
